import {useCallback} from 'react'

const FOCUS_VISIBLE_ATTR = 'data-focus-visible-within'

// Attribute set by the WICG focus-visible polyfill on elements it considers
// focus-visible. Honoring it lets this helper work in browsers that rely on the
// polyfill instead of native `:focus-visible`.
const FOCUS_VISIBLE_POLYFILL_ATTR = 'data-focus-visible-added'

/**
 * Returns whether `target` currently matches `:focus-visible`.
 *
 * Honors the WICG focus-visible polyfill's `data-focus-visible-added` attribute, and is
 * guarded so it is safe in non-browser environments (jsdom) that don't implement the
 * `:focus-visible` selector.
 *
 * Use this in a focus handler to reproduce keyboard-only styling that would otherwise
 * be written as `:has(:focus-visible)` / `:has(.x:focus-visible)`. A `:focus-within`
 * swap is not equivalent because it also reacts to pointer (mouse) focus.
 */
export function matchesFocusVisible(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  // If the focus-visible polyfill is active it owns the decision, so trust its attribute.
  if (target.hasAttribute(FOCUS_VISIBLE_POLYFILL_ATTR)) return true
  try {
    return target.matches(':focus-visible')
  } catch {
    // Extremely old browsers can't parse `:focus-visible`. The polyfill (checked above)
    // would have marked the element if active, so fail safe by showing the focus ring
    // (a missing keyboard ring is an accessibility failure). Note: modern jsdom doesn't
    // throw here — it returns false — so this branch is real-browser only.
    return true
  }
}

export type UseFocusVisibleWithinOptions = {
  /**
   * When set, the attribute is placed on the closest ancestor matching this selector
   * (scoped within the ref'd node) instead of on the node itself — e.g. `'li'` to mark
   * the focused row of a list.
   */
  closest?: string
}

/**
 * Returns a callback ref that toggles the `data-focus-visible-within` attribute on the
 * ref'd element (or, with `closest`, the nearest matching ancestor) while it contains a
 * `:focus-visible` descendant.
 *
 * This replaces `:has(:focus-visible)` selectors. `:has()` forces WebKit into expensive
 * style invalidation whenever the watched subtree mutates (severe on long or frequently
 * re-rendered lists); a single delegated focusin/focusout listener only touches the
 * affected element.
 */
export function useFocusVisibleWithin({closest}: UseFocusVisibleWithinOptions = {}) {
  return useCallback(
    (node: HTMLElement | null) => {
      if (!node) return

      const resolveTarget = (target: HTMLElement): HTMLElement | null => {
        if (!closest) return node
        const match = target.closest<HTMLElement>(closest)
        return match && node.contains(match) ? match : null
      }

      // Guarded so repeated focusin events that don't change the state (e.g. tabbing between
      // descendants of the same watched row) never write the attribute, avoiding redundant
      // style recalculation.
      const applyState = (element: HTMLElement, visible: boolean) => {
        if (visible) {
          if (!element.hasAttribute(FOCUS_VISIBLE_ATTR)) element.setAttribute(FOCUS_VISIBLE_ATTR, 'true')
        } else if (element.hasAttribute(FOCUS_VISIBLE_ATTR)) {
          element.removeAttribute(FOCUS_VISIBLE_ATTR)
        }
      }

      const onFocusIn = (event: FocusEvent) => {
        const target = event.target
        if (!(target instanceof HTMLElement)) return
        const element = resolveTarget(target)
        if (!element) return
        // Reconcile on every focusin so moving focus to a pointer-focused descendant clears the
        // attribute, matching `:has(:focus-visible)` (which only holds while a focus-visible
        // descendant is focused).
        applyState(element, matchesFocusVisible(target))
      }

      const onFocusOut = (event: FocusEvent) => {
        const target = event.target
        if (!(target instanceof HTMLElement)) return
        const element = resolveTarget(target)
        if (!element) return
        const nextFocused = event.relatedTarget
        if (nextFocused instanceof Node && element.contains(nextFocused)) return
        applyState(element, false)
      }

      node.addEventListener('focusin', onFocusIn)
      node.addEventListener('focusout', onFocusOut)

      return () => {
        node.removeEventListener('focusin', onFocusIn)
        node.removeEventListener('focusout', onFocusOut)
      }
    },
    [closest],
  )
}
